import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { BackgroundColorProps, BorderRadiusProps } from 'styled-system';
import { background, borderRadius, space, themeGet } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { RenderingType } from '../../../product-mvc-buttons/components/enums';
import type { GroupValue as GroupValueItem } from '../../../product-mvc-buttons/components/types';
import type { SpaceProps } from '../../theme/system-types';
import type { ListStyleProps, ThemeHeightProps } from '../../theme/system-utilities';
import { listStyle, themeHeight } from '../../theme/system-utilities';
import { extractKeywordWithAbstract, hasKeyword } from '../../utils/keyword';
import { Button } from '../Button';
import { Flex } from '../Flex';
import Icon from '../Icon';
import { HeadlineH8 } from '../Typography';
import GroupValue from './GroupValue';
import { useOpenInfoDrawer } from './MVCInfoDrawer';
import type { ChangeHandler, SelectedValue } from './types';

const INFO_ICON_SIZE = 32;

interface GroupValuesProps {
  sku: string;
  url: string;
  title: Nullable<string>;
  variationKey: Nullable<string>;
  listLength: number;
  items: GroupValueItem[];
  renderingType: RenderingType;
  selectedValue: Nullable<SelectedValue>;
  onChange: ChangeHandler;
  isVariationDrawer?: boolean;
}

const Header = styled(Flex)<ThemeHeightProps>`
  ${themeHeight};
`;
const headerStyles: ComponentProps<typeof Header> = {
  justifyContent: 'space-between',
  alignItems: 'center',
  themeHeight: 'sp_48',
};

const IconButton = styled(Button)<BackgroundColorProps & BorderRadiusProps>`
  ${background};
  ${borderRadius};

  svg {
    &:hover {
      background-color: ${themeGet('colors.sys.neutral.background.hover')};
    }
    &:active {
      background-color: ${themeGet('colors.sys.neutral.background.active')};
    }
  }
`;

const iconButtonStyles: Partial<ComponentProps<typeof IconButton>> = {
  borderRadius: '100%',
};

const InfoIcon = styled(Icon)<BorderRadiusProps>`
  ${borderRadius};
`;

const iconStyles: Partial<ComponentProps<typeof InfoIcon>> = {
  size: INFO_ICON_SIZE,
  borderRadius: '100%',
};

const List = styled.ul<SpaceProps & ListStyleProps>`
  ${space};
  ${listStyle};
`;
const listStyles: SpaceProps & ListStyleProps = {
  m: 'sp_0',
  mb: 'sp_8',
  p: 'sp_0',
  listStyle: 'none',
};

const extractLexicon = (title: GroupValuesProps['title']) => {
  const label = { keyword: title, abstract: null };

  if (!title || !hasKeyword(title)) {
    return label;
  }

  return extractKeywordWithAbstract(title);
};

const getSortedItems = (items: GroupValuesProps['items']) => {
  // eslint-disable-next-line complexity
  return items.sort((a, b) => {
    // 1. Other items (not disabled)
    if (!a.disabled && b.disabled) return -1;
    if (a.disabled && !b.disabled) return 1;

    // 2. Disabled & hasAlternatives: true
    if (a.disabled && a.hasAlternatives && b.disabled && !b.hasAlternatives) return -1;
    if (b.disabled && b.hasAlternatives && a.disabled && !a.hasAlternatives) return 1;

    // 3. Disabled & hasAlternatives: false
    return 0;
  });
};

const GroupValues = ({
  title,
  items,
  listLength,
  isVariationDrawer,
  ...props
}: GroupValuesProps) => {
  const { keyword, abstract } = useMemo(() => extractLexicon(title), [title]);
  const sortedItems = useMemo(() => getSortedItems(items), [items]);

  const openInfo = useOpenInfoDrawer({ headline: keyword, content: abstract });

  const { t } = useLocalization();

  return (
    <>
      {keyword && (
        <Header {...headerStyles}>
          <HeadlineH8>{keyword}</HeadlineH8>
          {abstract && (
            <IconButton
              kind="bare"
              {...iconButtonStyles}
              onClick={openInfo}
              aria-label={`${t('h24_more_info')} ${keyword}`}
            >
              <InfoIcon name="info" {...iconStyles} />
            </IconButton>
          )}
        </Header>
      )}

      <List {...listStyles}>
        {sortedItems.map(value => (
          <GroupValue
            {...props}
            key={value.key}
            groupValue={value}
            listLength={listLength}
            isVariationDrawer={isVariationDrawer}
          />
        ))}
      </List>
    </>
  );
};

export default GroupValues;
